import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type Me = { userRef: string; groups: string[]; teams: string[]; permissions: string[]; isPortalAdmin: boolean };
export type TeamRow = { team: string; member: boolean; pending: boolean; protected: boolean };
export type RequestStatus = 'requested' | 'processing' | 'approved' | 'rejected' | 'cancelled';
export type AccessRequest = {
  id: number; userRef: string; email: string; team: string; reason: string; status: RequestStatus;
  decidedBy: string | null; decidedReason: string | null; lastError: string | null; createdAt: string; updatedAt: string;
};
export type Member = { groupName: string; userRef: string; email: string; keycloakUserId: string; source: string; updatedAt: string };
export type AccessEvent = {
  id: number; at: string; actor: string; targetUser: string | null; subject: string; action: string; reason: string | null; keycloakResult: string | null;
};
export type MappingRow = { permission: string; groupName: string; createdBy: string; createdAt: string };

/** 서버 body `{code, message}` 를 그대로 들고 있다 (KEYCLOAK_FAILED·KEYCLOAK_DISABLED·SELF_APPROVAL 등을 code 로 분기) */
export class PortalAccessApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'PortalAccessApiError';
  }
}

export interface PortalAccessApi {
  me(): Promise<Me>;
  teams(): Promise<TeamRow[]>;
  createRequest(team: string, reason: string): Promise<{ id: number }>;
  myRequests(): Promise<AccessRequest[]>;
  cancelRequest(id: number): Promise<void>;
  requests(status?: RequestStatus[]): Promise<AccessRequest[]>;
  approve(id: number): Promise<{ status: string; message: string }>;
  reject(id: number, reason: string): Promise<void>;
  members(team: string): Promise<Member[]>;
  revoke(team: string, userRef: string, reason: string): Promise<void>;
  leave(team: string, reason: string): Promise<void>;
  approvers(): Promise<Member[]>;
  addApprover(team: string, userRef: string, reason: string): Promise<void>;
  removeApprover(team: string, userRef: string, reason: string): Promise<void>;
  events(limit?: number): Promise<AccessEvent[]>;
  mappings(): Promise<MappingRow[]>;
  addMapping(permission: string, groupName: string, reason: string): Promise<void>;
  removeMapping(permission: string, groupName: string, reason: string): Promise<void>;
}

export const portalAccessApiRef = createApiRef<PortalAccessApi>({ id: 'plugin.portal-access.service' });

const PLUGIN_ID = 'portal-access';
const enc = encodeURIComponent;

export class PortalAccessClient implements PortalAccessApi {
  constructor(private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {}

  private async call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const base = await this.deps.discoveryApi.getBaseUrl(PLUGIN_ID);
    const res = await this.deps.fetchApi.fetch(`${base}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new PortalAccessApiError(res.status, b.code ?? 'UNKNOWN', b.message ?? `요청이 실패했습니다 (HTTP ${res.status})`);
    }
    return res.json();
  }

  me = () => this.call<Me>('/me');
  teams = () => this.call<TeamRow[]>('/teams');
  createRequest = (team: string, reason: string) => this.call<{ id: number }>('/requests', 'POST', { team, reason });
  myRequests = () => this.call<AccessRequest[]>('/requests/mine');
  cancelRequest = async (id: number) => void (await this.call(`/requests/${id}/cancel`, 'POST'));
  requests = (status?: RequestStatus[]) => this.call<AccessRequest[]>(`/requests${status?.length ? `?status=${status.join(',')}` : ''}`);
  approve = (id: number) => this.call<{ status: string; message: string }>(`/requests/${id}/approve`, 'POST');
  reject = async (id: number, reason: string) => void (await this.call(`/requests/${id}/reject`, 'POST', { reason }));
  members = async (team: string) => (await this.call<{ rows: Member[] }>(`/teams/${enc(team)}/members`)).rows;
  revoke = async (team: string, userRef: string, reason: string) =>
    void (await this.call(`/teams/${enc(team)}/members/${enc(userRef)}/revoke`, 'POST', { reason }));
  leave = async (team: string, reason: string) => void (await this.call(`/teams/${enc(team)}/leave`, 'POST', { reason }));
  approvers = async () => (await this.call<{ rows: Member[] }>('/approvers')).rows;
  addApprover = async (team: string, userRef: string, reason: string) => void (await this.call(`/approvers/${enc(team)}`, 'POST', { userRef, reason }));
  removeApprover = async (team: string, userRef: string, reason: string) =>
    void (await this.call(`/approvers/${enc(team)}/${enc(userRef)}`, 'DELETE', { reason }));
  events = async (limit = 100) => (await this.call<{ rows: AccessEvent[] }>(`/events?limit=${limit}`)).rows;
  mappings = async () => (await this.call<{ rows: MappingRow[] }>('/mappings')).rows;
  addMapping = async (permission: string, groupName: string, reason: string) =>
    void (await this.call('/mappings', 'POST', { permission, groupName, reason }));
  removeMapping = async (permission: string, groupName: string, reason: string) =>
    void (await this.call(`/mappings/${enc(permission)}/${enc(groupName)}`, 'DELETE', { reason }));
}
